/**
 * Procesa un array en lotes para no sobrecargar el servidor
 * Ideal para operaciones masivas (cambio de estado, etc)
 *
 * @param {Array} items - Array de items a procesar
 * @param {Function} processor - Función que procesa cada lote: (batch) => Promise
 * @param {Object} options - Opciones
 *   - batchSize: items por request (default: 5)
 *   - onProgress: callback (processed, total) => void
 *   - onError: callback (error, batch, processed) => void
 *   - stopOnError: detener si hay error (default: false)
 * @returns {Promise} resolves con {success: count, errors: [{item, error}]}
 */
export async function processBatch(items, processor, options = {}) {
  const {
    batchSize = 5,
    onProgress = () => {},
    onError = () => {},
    stopOnError = false
  } = options;

  const results = {
    success: 0,
    errors: [],
    total: items.length
  };

  try {
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);
      const processed = Math.min(i + batchSize, items.length);

      try {
        // Procesar lote
        await processor(batch);
        results.success += batch.length;
        onProgress(processed, items.length);
      } catch (error) {
        // Manejar error del lote
        const errorData = {
          batch,
          error,
          processed
        };

        results.errors.push(errorData);
        onError(error, batch, processed);

        if (stopOnError) {
          break;
        }

        // Continuar con el siguiente lote aunque haya error
        onProgress(processed, items.length);
      }
    }
  } catch (error) {
    throw error;
  }

  return results;
}

/**
 * Helper para crear un toast de progreso
 * Usa Element UI Notification directamente para mejor control
 */
export function createProgressToast($notifications, title = "Procesando...") {
  let totalItems = 0;
  let notificationHandle = null;

  // Obtener Notification de Element UI
  const { Notification } = require("element-ui");

  return {
    show: total => {
      totalItems = total;

      const message = `
        <div style="display: flex; align-items: center; gap: 10px;">
          <i class="el-icon-loading"></i>
          <span>${title} (0/${total})</span>
        </div>
      `;

      notificationHandle = Notification.success({
        title: "",
        message: message,
        dangerouslyUseHTMLString: true,
        duration: 0,
        showClose: false,
        customClass: "batch-processing-toast",
        position: "top-right"
      });
    },

    update: processed => {
      // Cerrar notificación anterior y crear nueva con progreso actualizado
      if (notificationHandle && notificationHandle.close) {
        notificationHandle.close();
      }

      const message = `
        <div style="display: flex; align-items: center; gap: 10px;">
          <i class="el-icon-loading"></i>
          <span>${title} (${processed}/${totalItems})</span>
        </div>
      `;

      notificationHandle = Notification.success({
        title: "",
        message: message,
        dangerouslyUseHTMLString: true,
        duration: 0,
        showClose: false,
        customClass: "batch-processing-toast",
        position: "top-right"
      });
    },

    success: (count, errors = 0) => {
      // Cerrar notificación de progreso
      if (notificationHandle && notificationHandle.close) {
        notificationHandle.close();
        notificationHandle = null;
      }

      const msg =
        errors > 0
          ? `✓ Completado: ${count} éxito, ${errors} errores`
          : `✓ Completado: ${count} documentos procesados`;

      Notification.success({
        title: "",
        message: msg,
        duration: 4000,
        position: "top-right"
      });
    },

    error: (message = "Error al procesar") => {
      // Cerrar notificación de progreso
      if (notificationHandle && notificationHandle.close) {
        notificationHandle.close();
        notificationHandle = null;
      }

      Notification.error({
        title: "",
        message: message,
        duration: 5000,
        position: "top-right"
      });
    }
  };
}
